import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Icon,
  Link,
  Text as ChakraText,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiBrush3Fill } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserSessionResponse } from '../../interfaces'
import type {
  URLFailedListResponse,
  URLListErrorResponse,
  URLListSuccessResponse,
} from '../../interfaces/PurgeCacheResponse'
import RejectedURLModal from './RejectedURLModal'
import { checkPermission, PermissionsEnum } from '../../../utils'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  onClose(): void
  getData(): Promise<void>
}

interface FormProps {
  url: string
}

const PurgeCacheModal = ({
  isOpen,
  onClose,
  user,
  getData,
}: Props): React.ReactElement => {
  const toast = useToast()
  const { t } = useTranslation()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [rejectedURLs, setRejectedURLs] = useState<URLFailedListResponse[]>()
  const [formError, setFormError] = useState<string>('')

  const {
    isOpen: isOpenRejectedURL,
    onOpen: onOpenRejectedURL,
    onClose: onCloseRejectedURL,
  } = useDisclosure()

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setFormError('')
        }}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <Formik
            initialValues={{ url: '' }}
            onSubmit={async (values): Promise<void> => {
              setFormError('')
              const urlList: string[] = values.url.split('\n')

              if (urlList.length > 100) {
                setFormError(
                  t(
                    'cant_purge_more_than_100',
                    "Can't purge more than 100 images at a time",
                  ),
                )
              }

              if (user?.clientNumber) {
                const purgeCacheEndpoint = `/api/clients/${user.clientNumber}/purge-cache/url-list${
                  checkPermission({ user, permission: PermissionsEnum.CLIENT_MANAGE }) ? '-admin' : ''
                }`;
                const response = (await request.post(
                  purgeCacheEndpoint,
                  { urlList, imagekitId: user.imagekitId },
                )) as
                  | URLFailedListResponse[]
                  | URLListSuccessResponse
                  | URLListErrorResponse
                if (request.error) {
                  if ((response as URLListErrorResponse).message) {
                    setFormError((response as URLListErrorResponse).message)
                  } else {
                    setFormError(
                      t(
                        'error_occured_in_purging_cache',
                        'An unknown error occured in purging cache for URL',
                      ),
                    )
                  }
                } else {
                  setFormError('')
                  if ((response as URLListSuccessResponse).success) {
                    toast({
                      description: t(
                        'urls_successfully_submitted_for_purging',
                        'URLs successfully submitted for purging',
                      ),
                      status: 'success',
                      isClosable: true,
                      duration: 5000,
                      position: 'top-right',
                      variant: 'outline',
                    })
                    onClose()
                  } else {
                    setRejectedURLs(response as URLFailedListResponse[])
                    onClose()
                    onOpenRejectedURL()
                  }
                  await getData()
                }
              }
            }}
            validationSchema={Yup.object().shape({
              url: Yup.string().required('Required'),
            })}
          >
            {({ handleChange, isSubmitting, submitForm }) => (
              <>
                <ModalHeader>
                  <Icon
                    as={RiBrush3Fill}
                    color={colors.appBattleshipGrey}
                    backgroundColor={colors.appPaleGrey}
                    boxSize={6}
                    padding="0.1em"
                    borderRadius="50%"
                    marginRight="0.5em"
                  />
                  {t('new_clear_cache_request', 'New Clear Cache Request')}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {formError ? (
                    <Alert status="error" mt="1em">
                      <AlertIcon />
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  ) : null}
                  <Field name="url">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginTop="1em"
                        isInvalid={Boolean(form.errors.url && form.touched.url)}
                        isRequired
                      >
                        <FormLabel htmlFor="url">
                          {t('image_url', 'Image URL')}
                        </FormLabel>
                        <Trans t={t} i18nKey="purge_cache_modal_description">
                          <ChakraText
                            color="appBattleshipGrey"
                            padding="0.8em 0"
                          >
                            New Clear Cache Request. The complete URL of image
                            including http:// or https:// e.g.
                            https://ik.imagekit.io/demo/default-image.jpg.{' '}
                            <Link
                              href="https://docs.imagekit.io/features/cache-purging"
                              isExternal
                            >
                              Learn more
                            </Link>
                            .
                          </ChakraText>
                        </Trans>
                        <Textarea
                          {...field}
                          id="url"
                          placeholder={t(
                            'purge_cache_modal_textarea_placeholder',
                            'Enter Multiple URLs separated by new line',
                          )}
                          onChange={handleChange}
                          rows={5}
                        />
                        <FormErrorMessage marginBottom="-2.2em">
                          {form.errors.url}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </ModalBody>
                <ModalFooter
                  borderTop="1px solid #e7eaf0"
                  style={{ gap: '1em' }}
                >
                  <Button
                    onClick={() => {
                      onClose()
                      setFormError('')
                    }}
                    variant="outline"
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    onClick={() => {
                      void submitForm()
                    }}
                  >
                    {t('submit', 'Submit')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </Formik>
        </ModalContent>
      </Modal>
      <RejectedURLModal
        data={rejectedURLs}
        onClose={onCloseRejectedURL}
        isOpen={isOpenRejectedURL}
      />
    </>
  )
}

export default PurgeCacheModal
